import spartaEventsUtility from '@sparta.global.utilities/sparta-events-utility/dist/es6.js';
import spartaGlobalFunctions from '@sparta.global.utilities/sparta-global-functions-utility/dist/es6.js';

/* eslint-disable brace-style */


// Added as a class to the container element the instance is invoked on.
const utilName = 'focus-trap-utility';
const fontStyle = 'cnx-regular';
const EVENTS = {
  FOCUSOUT: `FOCUSOUT.${utilName}`
};

/**
 * Makes a focusable div with given `innerHTML`.
 * @param {string} htmlContent - HTML content of the div to be created.
 * @param {string} className - Class to be applied to the div to be created.
 * @param {boolean} ariaHidden - Determines if screen readers can access boundary.
 * @returns {node} Focusable div.
 */
const createBoundaryElement = (htmlContent, className, ariaHidden) => {
  const bound = document.createElement('div');
  bound.innerHTML = htmlContent;
  bound.classList.add(className);
  bound.classList.add('ada-hidden');
  bound.classList.add(fontStyle);
  bound.setAttribute('aria-hidden', !!ariaHidden);
  bound.setAttribute('tabindex', '-1');
  return bound;
};

/**
 * Utility to contain user focus navigation (tab/swipe) within a given container.
 */
class FocusTrap {
  /**
   * Sets up instance-specific utility attributes.
   * @param {object} args - Object to contain reference to container optional override HTML content.
   * @returns {void}
   */
  constructor(args = {}) {
    // text to be read when focus is moved
    this.layerBeginText = args.layerBeginText;
    this.layerEndText = args.layerEndText;
    this.hideBoundText = !!args.hideBoundText || false;

    // container user will be confined to
    this.setContainer(args.container, args.containerId || args.containerID);
    this.setBounds();

    // attributes that will be set by attaching the instance to a container
    this.previousFocus = null;

    // prebind callbacks so they can be removed
    this.focusOut = this.focusOutCallback.bind(this);
    this.focusIn = this.focusInCallback.bind(this);
  }

  /**
   * Creates the elements that will be used as focus boundaries.
   * @param {string} layerBeginText - Text for the top boundary.
   * @param {string} layerEndText - Text for the bottom boundary.
   * @returns {void}
   */
  setBounds(layerBeginText = this.layerBeginText, layerEndText = this.layerEndText) {
    this.topBound = this.addClickHandler(createBoundaryElement(layerBeginText, 'focus-boundary-top', this.hideBoundText));
    this.bottomBound = this.addClickHandler(createBoundaryElement(layerEndText, 'focus-boundary-bottom', this.hideBoundText));
    this.bounceBound = createBoundaryElement('', 'focus-boundary-bounce', true);
  }

  /**
   * Safely sets the classList of the container.
   * @param {string} [containerClass] - String that will be added to the container.
   * @param {node} [container] - Element to which the class will be added.
   * @returns {instance} This instance of the focus trap.
   */
  setClasslist(containerClass = utilName, container = this.container) {
    if (container && container.classList) {
      container.classList.add(containerClass);
    }
    return this;
  }

  /**
   * Resets the focus trap container without creating a new instance.
   * @param {node} container - DOM node of container to attach trap to.
   * @param {string} containerId - ID of the container to be used when `container` is not available.
   * @returns {instance} This instance of the focus trap.
   */
  setContainer(container, containerId) {
    this.removeTrap();
    this.container = container || document.getElementById(containerId);
    this.setClasslist();
    return this;
  }

  /**
   * Detects if the previous active element was a countainer boundary.
   * @returns {boolean} Whether the focus came from container boundary.
   */
  wasBounced() {
    return this.previousFocus === this.bottomBound || this.previousFocus === this.topBound;
  }

  /**
   * Checks where the focus should be sent after `focusout` fires on container boundary.
   * @param {object} event - DOM event from the `focusout`.
   * @returns {node} Container boundary which the focus is to be sent to.
   */
  getFocusoutTarget(event) {
    return event.target === this.topBound ? this.bottomBound : this.topBound;
  }

  /**
   * Checks if user is leaving container and wrangles focus back to container.
   * @param {event} event - `focusout` DOM event.
   * @returns {object} Instance of `FocusTrap` class.
   */
  focusOutCallback(event) {
    if (!this.wasBounced()) {
      const nextElem = event.relatedTarget || document.activeElement;
      if (!this.container.contains(nextElem)) {
        const targetElem = this.getFocusoutTarget(event);
        targetElem.focus();
        this.previousFocus = nextElem;
        spartaEventsUtility.pub(EVENTS.FOCUSOUT, {
          event
        });
      }
    }
    return this;
  }

  /**
   * Bounces focus from bottommost boundary to the top.
   * @returns {object} Instance of `FocusTrap` class.
   */
  focusInCallback() {
    this.topBound.focus();
    return this;
  }

  /**
   * Binds `keydown` events to focus trap via keyboard.
   * @param {object} object - DOM element passed through`
   * @returns {void}
   */
  modalClickEvent(object) {
    const {
      container,
      topBound,
      bottomBound
    } = object;
    object.container.addEventListener('keydown', event => {
      const thisKeyCode = event.charCode || event.keyCode;
      const trigger = event.target || event.srcElement;

      // Adding tab order if modal has scrollable content
      let modalContentTab = false;
      let nonTabbables;
      let tabbables;
      if (container.classList.contains('spa-dialog')) {
        nonTabbables = [...container.querySelectorAll('.content')];
        const modalContent = nonTabbables && nonTabbables[nonTabbables.length - 1];
        if (modalContent.hasAttribute('tabindex')) {
          modalContentTab = true;
        }
      }

      // get tabbable elements to cross reference.
      if (modalContentTab) {
        tabbables = [...container.querySelectorAll('button, [href], input, select, textarea, .content')];
      } else {
        tabbables = [...container.querySelectorAll('button, [href], input, select, textarea')];
      }

      // Remove elements that are hidden from view.
      tabbables.forEach(element => {
        const isHidden = element.offsetParent === null;
        if (isHidden) {
          const tabbableIndex = Array.from(tabbables).indexOf(element);
          if (tabbables) {
            tabbables.splice(tabbableIndex, 1);
          }
        }
      });

      // Get last tabbable element.
      const tabbablesLast = tabbables[tabbables.length - 1];

      // if shift + tab
      if (event.shiftKey && event.key === 'Tab') {
        // if bottom trap is focused send focus to last tabbable item
        if (trigger === bottomBound) {
          event.preventDefault();
          tabbablesLast.focus();
        }
        // if top trap is focused send focus to bottom trap
        else if (trigger === topBound) {
          event.preventDefault();
          bottomBound.focus();
        }
      }
      // on tab press
      else if (thisKeyCode === 9) {
        // if last tabbable item is focused send focus to bottom trap
        if (trigger === tabbablesLast) {
          event.preventDefault();
          bottomBound.focus();
        }
        // if bottom trap is focused send focus to top trap
        else if (trigger === bottomBound) {
          event.preventDefault();
          topBound.focus();
        }
      }
    });
  }

  /**
   * Attaches or removes event listeners on array of DOM nodes.
   * @param {string} eventAction - Name of listener method.
   * @returns {object} Instance of `FocusTrap` class.
   */
  modifyBoundEvents(eventAction) {
    this.container[eventAction]('focusout', this.focusOut, true);
    this.container[eventAction]('keydown', this.modalClickEvent(this), true);
    this.bounceBound[eventAction]('focusin', this.focusIn, true);
    return this;
  }

  /**
   * Leverages `modifyBoundEvents` method to add event listeners to nodes.
   * @returns {object} Instance of `FocusTrap` class (as returned by `modifyBoundEvents`).
   */
  attachFocusListeners() {
    return this.modifyBoundEvents('addEventListener');
  }

  /**
   * Leverages `modifyBoundEvents` method to remove event listeners from nodes.
   * @returns {object} Instance of `FocusTrap` class (as returned by `modifyBoundEvents`).
   */
  removeFocusListeners() {
    return this.modifyBoundEvents('removeEventListener');
  }

  /**
   * Adds a click handler to a given DOM element.
   * @param {node} target - The target DOM element.
   * @returns {object} Instance of `FocusTrap` class.
   */
  addClickHandler(target) {
    target.addEventListener('click', event => {
      this.getFocusoutTarget(event).focus();
    });
    return target;
  }

  /**
   * Modifies focus trap text to include layer type.
   * Text is pulled from global params with English language default.
   * @returns {void}
   */
  createBoundText() {
    // Set layer begin text.
    let layerBeginText = this.layerBeginText ? this.layerBeginText : spartaGlobalFunctions.getGlobalParams('layerStart') || 'Start of [layerType] content';

    // Set layer end text.
    let layerEndText = this.layerEndText ? this.layerEndText : spartaGlobalFunctions.getGlobalParams('layerEnd') || 'End of [layerType] content';

    // Update with nicename of layer type.
    let typeNiceName = '';
    const inLayer = this.container.closest('.spa-ui-layer');
    if (inLayer) {
      typeNiceName = inLayer.classList.contains('spa-ui-modal') ? 'modal' : 'popup';
    }
    layerBeginText = layerBeginText.replace('[layerType]', typeNiceName.toLowerCase()).trim();
    layerEndText = layerEndText.replace('[layerType]', typeNiceName.toLowerCase()).trim();

    // Update utility global variables.
    this.layerBeginText = layerBeginText;
    this.layerEndText = layerEndText;
  }

  /**
   * Creates, injects, and stores container boundaries.
   * @param {string} [layerBeginText] - DOM string injected into the upper container boundary.
   * @param {string} [layerEndText] - DOM string injected into the lower container boundary.
   * @returns {object} Instance of `FocusTrap` class.
   */
  addBounds() {
    // Update strings from global params.
    this.createBoundText();
    this.topBound.innerHTML = this.layerBeginText;
    this.bottomBound.innerHTML = this.layerEndText;

    // Append elements to DOM.
    this.container.insertBefore(this.topBound, this.container.firstElementChild);
    this.container.appendChild(this.bottomBound);
    this.container.appendChild(this.bounceBound);
    return this;
  }

  /**
   * Node removal with existence check.
   * @param {node} item - Item to be removed from the container.
   * @param {node} container - Container item must be child of.
   * @returns {object} This instance of the class.
   */
  removeItem(item, container = this.container) {
    if (item && container.contains(item)) {
      container.removeChild(item);
    }
    return this;
  }

  /**
   * Removes container boundaries from containers.
   * @param {node} topBound - Top focusable trap element to be removed.
   * @param {node} bottomBound - Bottom focusable trap element to be removed.
   * @returns {object} Instance of `FocusTrap` class.
   */
  removeBounds() {
    const {
      topBound,
      bottomBound,
      bounceBound
    } = this;
    this.removeItem(topBound).removeItem(bottomBound).removeItem(bounceBound);
    return this;
  }

  /**
   * Adds class list to container, attached event listeners, and adds boundary elements.
   * @param {node} container - Can change the container the trap is attached to.
   * @returns {object} Instance of `FocusTrap` class.
   */
  attachTrap() {
    this.setClasslist();
    this.addBounds();
    this.attachFocusListeners();
    this.container.setAttribute('tabindex', -1);
    return this;
  }

  /**
   * Removes utility class from container, removes event listeners, and deletes boundary elements.
   * @param {string} [containerClass] - Class to add and remove from the focus container.
   * @returns {object} Instance of `FocusTrap` class.
   */
  removeTrap(containerClass = utilName) {
    if (this.container) {
      this.container.classList.remove(containerClass);
      this.removeFocusListeners();
      this.removeBounds();
    }
    return this;
  }
}
const createFocusTrap = args => new FocusTrap(args);
createFocusTrap.EVENTS = EVENTS;

export { createFocusTrap as default };
